import { useRef, useState } from 'react';

import ErrorBoundary from 'components/ErrorBoundary';
import JulaLogo from 'components/JulaLogo';
import {
	type FieldKey,
	useDisclosureWidgetClose,
	useIsCompanySite,
	useIsScrolledToElement,
} from 'hooks';
import { useRouteChange } from 'hooks/navigation';
import type {
	Link as LinkType,
	MainLink,
	PageHeaderReview,
	USP,
} from 'models/sitecore';
import type { TemplateString } from 'types';
import { useI18n } from 'utils/i18n';

import PageHeaderMain from './PageHeaderMain';
import PageHeaderNav from './PageHeaderNav';
import PageHeaderTopBar from './PageHeaderTopBar';
import StoreSelectorPopover from './StoreSelectorPopover';

interface Props {
	accountMenuButtonLabel: string;
	cartButtonLabel: string;
	/** Number of products in the cart */
	cartQuantity?: number;

	inspirationLinksHeading: string;
	inspirationMediaHeading: string;
	/** If the user is logged in */
	isAuthenticatedUser?: boolean;
	/** If the user is being initialised */
	isInitialisingUser?: undefined | boolean;

	/** Main menu links */
	mainMenu?: MainLink[];

	mobileMenuButtonLabel: string;

	mobileMenuCloseText: string;

	navigationCategoryShowAllButton: TemplateString | FieldKey;
	/** Click handler for logged in account button */
	onAccountClick: () => void;

	/** Click handler for login button */
	onLoginClick: () => void;

	popularProductsText: string;

	popularSearchTermsText: string;

	primaryMenuLabel: string;
	/** Menu links for the top bar */
	primaryTopLinks?: LinkType[];
	review?: PageHeaderReview | undefined;
	searchBrandSuggestionLabel: string;
	searchCancelButtonText: string;
	searchCategorySuggestionLabel: string;
	searchFieldLabel: string;
	searchFieldPlaceholderText: string;
	searchInspirationSuggestionLabel: string;
	searchNoResultsText: string;
	searchOpenSearchButtonLabel: string;
	searchOtherSearchTermRecommendationsText: string;
	searchResultProductsText: string;
	searchResultsSpellingSuggestionText: string;
	searchResultsText: string;
	searchStoreSuggestionLabel: string;
	searchSuggestionLabel: string;
	/** Secondary links for the menu row */
	secondaryTopLinks?: LinkType[];

	storePopoverChangeStoreTitle: string;

	storePopoverSelectStoreTitle: string;
	topBarMenuLabel: string;

	/** USP texts */
	uspLinks?: USP[];
	wishlistButtonLabel: string;
	/** Wishlist product quantity */
	wishlistQuantity?: number;
}

/** Site page header */
export default function PageHeader({
	accountMenuButtonLabel,
	cartButtonLabel,
	cartQuantity,
	inspirationLinksHeading,
	inspirationMediaHeading,
	isAuthenticatedUser,
	isInitialisingUser,
	mainMenu,
	mobileMenuButtonLabel,
	mobileMenuCloseText,
	navigationCategoryShowAllButton,
	onAccountClick,
	onLoginClick,
	popularProductsText,
	popularSearchTermsText,
	primaryMenuLabel,
	primaryTopLinks,
	review,
	searchBrandSuggestionLabel,
	searchCancelButtonText,
	searchCategorySuggestionLabel,
	searchFieldLabel,
	searchFieldPlaceholderText,
	searchInspirationSuggestionLabel,
	searchNoResultsText,
	searchOpenSearchButtonLabel,
	searchOtherSearchTermRecommendationsText,
	searchResultProductsText,
	searchResultsSpellingSuggestionText,
	searchResultsText,
	searchStoreSuggestionLabel,
	searchSuggestionLabel,
	secondaryTopLinks,
	storePopoverChangeStoreTitle,
	storePopoverSelectStoreTitle,
	topBarMenuLabel,
	uspLinks,
	wishlistButtonLabel,
	wishlistQuantity,
}: Props) {
	const { t } = useI18n();
	const isCompanySite = useIsCompanySite();
	const toggledMenuCloseButtonRef = useRef<HTMLButtonElement>(null);
	const [afterHeaderRef, isScrolledPastHeader] =
		useIsScrolledToElement<HTMLDivElement>();
	const [isToggledMenuOpen, setIsToggledMenuOpen] = useState(false);
	const [isStoreSelectorOpen, setIsStoreSelectorOpen] = useState(false);

	useRouteChange(() => setIsToggledMenuOpen(false));

	/* ---------- Toggled menu for smaller screens ---------- */
	const handleToggledMenuClose = () => {
		setIsToggledMenuOpen(false);
	};
	const {
		onTriggerClick: onMenuTriggerClick,
		onTriggerMouseDown: onMenuTriggerMouseDown,
		triggerRef: menuToggleRef,
		widgetRef: toggledMenuContainerRef,
	} = useDisclosureWidgetClose<HTMLButtonElement, HTMLDivElement>(
		isToggledMenuOpen,
		handleToggledMenuClose,
	);
	const handleMenuToggleClick = () => {
		setIsToggledMenuOpen((current) => !current);
		onMenuTriggerClick();
		// Must wait for the menu to be displayed before focus can be moved.
		// Safari is especially fussy and requires a longer timeout than others.
		setTimeout(() => {
			toggledMenuCloseButtonRef.current?.focus();
		}, 150);
	};
	const handleToggledMenuCloseClick = () => {
		setIsToggledMenuOpen(false);
		menuToggleRef.current?.focus();
	};

	return (
		<>
			{/* Print only logo */}
			<div className="mb-4 hidden print:block">
				<JulaLogo
					title={isCompanySite ? t('company_site_logo_title') : undefined}
				/>
			</div>
			{/*
			Use `display: contents` to allow the sticky part (PageHeaderMain) to
			stick around for the entire page.
			For a long time this has been bad for a11y, removing the node completely
			from the accessibility tree. As of Mars 2023, the header element doesn't
			seem to have any problems in Safari 16.3, Chrome 111 or Firefox 110 on
			macOS when testing with VoiceOver.
			*/}
			<header data-testid="PageHeader" className="contents print:hidden">
				<ErrorBoundary isPageWidth>
					<PageHeaderTopBar
						topBarMenuLabel={topBarMenuLabel}
						uspLinks={uspLinks}
						menuLinks={primaryTopLinks}
						review={review}
					/>
				</ErrorBoundary>

				<ErrorBoundary isPageWidth>
					<PageHeaderMain
						wishlistButtonLabel={wishlistButtonLabel}
						cartButtonLabel={cartButtonLabel}
						accountMenuButtonLabel={accountMenuButtonLabel}
						cartQuantity={cartQuantity}
						isInitialisingUser={isInitialisingUser}
						isAuthenticatedUser={isAuthenticatedUser}
						isScrolledPastHeader={isScrolledPastHeader}
						isToggledMenuOpen={isToggledMenuOpen}
						menuToggleRef={menuToggleRef}
						onAccountClick={onAccountClick}
						onLoginClick={onLoginClick}
						onMenuToggleClick={handleMenuToggleClick}
						onMenuToggleMouseDown={onMenuTriggerMouseDown}
						mobileMenuButtonLabel={mobileMenuButtonLabel}
						searchBrandSuggestionLabel={searchBrandSuggestionLabel}
						searchCancelButtonText={searchCancelButtonText}
						searchCategorySuggestionLabel={searchCategorySuggestionLabel}
						searchFieldPlaceholderText={searchFieldPlaceholderText}
						searchInspirationSuggestionLabel={searchInspirationSuggestionLabel}
						searchOpenSearchButtonLabel={searchOpenSearchButtonLabel}
						searchOtherSearchTermRecommendationsText={
							searchOtherSearchTermRecommendationsText
						}
						popularProductsText={popularProductsText}
						popularSearchTermsText={popularSearchTermsText}
						searchResultProductsText={searchResultProductsText}
						searchResultsText={searchResultsText}
						searchResultsSpellingSuggestionText={
							searchResultsSpellingSuggestionText
						}
						searchFieldLabel={searchFieldLabel}
						searchNoResultsText={searchNoResultsText}
						searchStoreSuggestionLabel={searchStoreSuggestionLabel}
						searchSuggestionLabel={searchSuggestionLabel}
						wishlistQuantity={wishlistQuantity}
						onStoreSelectorClick={() => setIsStoreSelectorOpen(true)}
					/>
				</ErrorBoundary>

				<ErrorBoundary isPageWidth>
					<PageHeaderNav
						primaryMenuLabel={primaryMenuLabel}
						inspirationMediaHeading={inspirationMediaHeading}
						inspirationLinksHeading={inspirationLinksHeading}
						isToggledMenuOpen={isToggledMenuOpen}
						mainMenu={mainMenu}
						mobileMenuCloseText={mobileMenuCloseText}
						navigationCategoryShowAllButton={navigationCategoryShowAllButton}
						onToggledMenuCloseClick={handleToggledMenuCloseClick}
						popularProductsText={popularProductsText}
						popularSearchTermsText={popularSearchTermsText}
						secondaryMenu={secondaryTopLinks}
						smallScreenSecondaryMenuExtra={primaryTopLinks}
						toggledMenuContainerRef={toggledMenuContainerRef}
						toggledMenuCloseButtonRef={toggledMenuCloseButtonRef}
						onStoreSelectorClick={() => setIsStoreSelectorOpen(true)}
					/>
				</ErrorBoundary>
			</header>
			<div ref={afterHeaderRef} />
			<StoreSelectorPopover
				isOpen={isStoreSelectorOpen}
				onClose={() => setIsStoreSelectorOpen(false)}
				storePopoverChangeStoreTitle={storePopoverChangeStoreTitle}
				storePopoverSelectStoreTitle={storePopoverSelectStoreTitle}
			/>
		</>
	);
}
PageHeader.displayName = 'PageHeader';
